import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ComponentType } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  brokerSusep,
  brokers,
  commissions,
  integrationCalls,
  members,
  operations,
  termAcceptances,
  type Broker,
  type BrokerNomination,
  type IntegrationCall,
  type Member,
  type TermsAcceptance,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatCpfCnpj,
  formatDate,
  formatDateTime,
  formatDocument,
  formatMoney,
  formatPhone,
} from '@/shared/lib/format';
import { nomeDoProvedor } from '@/shared/lib/nomeDoProvedor';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import {
  DetailCard,
  DetailError,
  DetailLayout,
  DetailLoading,
  DetailTabs,
} from '@/shared/ui/DetailLayout';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { HistoryTab } from '@/shared/ui/HistoryTab';
import { ConfirmDialog } from '@/shared/ui/Modal';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { statusLabel } from '@/shared/ui/statusLabels';
import { useToast } from '@/shared/ui/useToast';

import { BlockBrokerDialog } from './BlockBrokerDialog';
import { BrokerAddressesCard } from './BrokerAddressesCard';
import {
  BrokerCommissionCheckCard,
  BrokerConsultationLimitCard,
} from './BrokerConsultationSection';
import { BrokerDocumentsSection } from './BrokerDocumentsSection';
import { BrokerFinancialSection } from './BrokerFinancialSection';
import { BrokerPartnersSection } from './BrokerPartnersSection';
import { BrokerRegistrationForm } from './BrokerRegistrationForm';
import { avisosDoLimite, useLimiteDeConsultas } from './limiteDeConsultas';
import {
  PENDENCIA_DATA_NOMEACAO,
  PENDENCIA_DIGITAL,
  PERMISSAO_DIGITAL,
  PERMISSAO_EDITAR,
  PERMISSAO_EXCLUIR,
  PERMISSAO_VER_ACEITES,
  rotuloDaAcao,
  SEM_PERMISSAO_TERMOS,
  tiposDeNomeacao,
} from './rotulos';

type SecaoProps = { corretor: Broker; onMudou: () => void };

interface SecaoDoMenu {
  chave: string;
  titulo: string;
  Componente: ComponentType<SecaoProps>;
}

// --- Cadastro Completo --------------------------------------------------------

type AbaDoCadastro = 'cadastrais' | 'financeiros' | 'usuarios' | 'documentos';

/**
 * Secao "Cadastro Completo", com as subabas do OnPoint na ordem dele: Dados
 * cadastrais, Dados financeiros, Usuarios e Documentos. Os enderecos ficam com os
 * dados cadastrais, de onde o principal vem.
 */
function CadastroCompleto({ corretor, onMudou }: SecaoProps) {
  const { can } = useAuth();
  const [aba, setAba] = useState<AbaDoCadastro>('cadastrais');
  const abas: { key: AbaDoCadastro; title: string }[] = [
    { key: 'cadastrais', title: 'Dados cadastrais' },
    { key: 'financeiros', title: 'Dados financeiros' },
    ...(can('usuarios.usuario.view')
      ? [{ key: 'usuarios' as const, title: 'Usuarios' }]
      : []),
    ...(can('documentos.documento.view')
      ? [{ key: 'documentos' as const, title: 'Documentos' }]
      : []),
  ];
  return (
    <div className="flex flex-col gap-4">
      <DetailTabs
        tabs={abas}
        active={aba}
        onChange={setAba}
        label="Cadastro Completo"
      />
      {aba === 'cadastrais' ? (
        <>
          {/* A chave refaz o formulario quando o corretor gravado muda, para que ele
              nunca mostre um valor que o servidor ja substituiu. */}
          <BrokerRegistrationForm key={corretor.updated_at} corretor={corretor} />
          <BrokerAddressesCard
            corretoraId={corretor.id}
            podeEditar={can(PERMISSAO_EDITAR)}
            onMudou={onMudou}
          />
          {/* P-COR-3: o limite de consultas (COR-033) e a validacao de comissao
              contra o limite (COR-216), as duas so informativas. */}
          <BrokerConsultationLimitCard corretor={corretor} />
          <BrokerCommissionCheckCard corretor={corretor} />
        </>
      ) : null}
      {aba === 'financeiros' ? (
        <BrokerFinancialSection key={corretor.updated_at} corretor={corretor} />
      ) : null}
      {aba === 'usuarios' ? <UsuariosDaCorretora corretor={corretor} /> : null}
      {aba === 'documentos' ? (
        <BrokerDocumentsSection corretoraId={corretor.id} />
      ) : null}
    </div>
  );
}

function UsuariosDaCorretora({ corretor }: { corretor: Broker }) {
  const consulta = useQuery({
    queryKey: ['members', 'por-corretora', corretor.id],
    queryFn: ({ signal }) =>
      members.list({ broker_id: corretor.id, limit: 100 }, signal),
  });
  return (
    <DetailCard
      title="Usuarios"
      description="Pessoas ligadas a corretora. O convite e o perfil ficam em Usuarios."
      loading={consulta.isPending}
      error={consulta.error}
    >
      <DataTable
        columns={[
          { key: 'nome', header: 'Nome', render: (pessoa: Member) => pessoa.name },
          { key: 'email', header: 'E-mail', render: (pessoa: Member) => pessoa.email },
          {
            key: 'telefone',
            header: 'Telefone',
            hideBelow: 'md',
            render: (pessoa: Member) => (pessoa.phone ? formatPhone(pessoa.phone) : ''),
          },
          {
            key: 'ativo',
            header: 'Ativo',
            render: (pessoa: Member) => (pessoa.status === 'active' ? 'Sim' : 'Nao'),
          },
          {
            key: 'perfil',
            header: 'Perfil',
            render: (pessoa: Member) =>
              pessoa.roles.map((papel) => papel.name).join(', '),
          },
        ]}
        items={consulta.data ?? []}
        keyOf={(pessoa) => pessoa.id}
        empty={<EmptyState title="Nenhuma pessoa ligada a corretora" />}
      />
    </DetailCard>
  );
}

// --- Nomeacoes ----------------------------------------------------------------

function Nomeacoes({ corretor }: SecaoProps) {
  const consulta = useQuery({
    queryKey: ['broker', corretor.id, 'nominations'],
    queryFn: ({ signal }) => brokers.nominations(corretor.id, signal),
  });
  return (
    <DetailCard
      title="Nomeacoes"
      description="Tomadores cujo corretor e esta corretora, com a validade e o tipo da ficha do tomador."
      loading={consulta.isPending}
      error={consulta.error}
    >
      <DataTable
        columns={[
          {
            key: 'acoes',
            header: 'Acoes',
            render: (linha: BrokerNomination) => (
              <Link
                to={`/tomadores/${linha.policyholder_id}`}
                className="text-xs font-medium text-brand-700 hover:underline"
              >
                Abrir tomador
              </Link>
            ),
          },
          {
            key: 'cnpj',
            header: 'CNPJ',
            render: (linha: BrokerNomination) => formatCpfCnpj(linha.document),
          },
          {
            key: 'razao',
            header: 'Razao Social',
            render: (linha: BrokerNomination) => linha.legal_name,
          },
          {
            key: 'data',
            header: 'Data de nomeacao',
            render: (linha: BrokerNomination) =>
              linha.nominated_on ? (
                formatCivilDate(linha.nominated_on)
              ) : (
                <span className="text-ink-400" title={PENDENCIA_DATA_NOMEACAO}>
                  nao informada
                </span>
              ),
          },
          {
            key: 'validade',
            header: 'Validade de nomeacao',
            render: (linha: BrokerNomination) => formatCivilDate(linha.valid_until),
          },
          {
            key: 'tipo',
            header: 'Tipo de nomeacao',
            render: (linha: BrokerNomination) =>
              tiposDeNomeacao[linha.instrument] ?? '',
          },
        ]}
        items={consulta.data ?? []}
        keyOf={(linha) => linha.policyholder_id}
        empty={<EmptyState title="Nenhum tomador nomeado para a corretora" />}
      />
    </DetailCard>
  );
}

// --- Socios, termos, historicos -------------------------------------------------

function Socios({ corretor }: SecaoProps) {
  const { can, session } = useAuth();
  return (
    <BrokerPartnersSection
      corretoraId={corretor.id}
      podeEditar={can(PERMISSAO_EDITAR) && session?.broker_scoped !== true}
    />
  );
}

/**
 * Termos de Aceite da corretora (COR-028, COR-095 a COR-097; item R-5 da auditoria de
 * paridade): os aceites do termo de uso das pessoas da corretora, pelo registro do
 * P-USU-2 (`GET /term-acceptances?broker_id=`), com Corretor, Data e hora da criacao
 * e o Termo de aceite para baixar, como no OnPoint. A pessoa e a versao sao do
 * SeguraCore.
 */
function TermosDeAceite({ corretor }: SecaoProps) {
  const { can } = useAuth();
  const toast = useToast();
  const [baixando, setBaixando] = useState<string | null>(null);
  const podeVer = can(PERMISSAO_VER_ACEITES);
  const consulta = useQuery({
    queryKey: ['term-acceptances', 'corretora', corretor.id],
    queryFn: ({ signal }) =>
      termAcceptances.list(
        { broker_id: corretor.id, limit: 100, sort: '-accepted_at' },
        signal,
      ),
    enabled: podeVer,
  });

  async function baixar(aceite: TermsAcceptance) {
    setBaixando(aceite.id);
    try {
      const arquivo = await termAcceptances.download(aceite.id);
      const extensao = arquivo.type.includes('pdf') ? 'pdf' : 'docx';
      const url = URL.createObjectURL(arquivo);
      const link = document.createElement('a');
      link.href = url;
      link.download = `aceite-termo-de-uso-v${aceite.version}.${extensao}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (erro) {
      toast.error(
        erro instanceof Error ? erro.message : 'Nao foi possivel baixar o termo.',
      );
    } finally {
      setBaixando(null);
    }
  }

  if (!podeVer) {
    return (
      <DetailCard title="Termos de Aceite">
        <EmptyState title="Sem acesso aos aceites" description={SEM_PERMISSAO_TERMOS} />
      </DetailCard>
    );
  }
  return (
    <DetailCard
      title="Termos de Aceite"
      description="Os aceites do termo de uso das pessoas da corretora, com o comprovante de cada um."
      loading={consulta.isPending}
      error={consulta.error}
    >
      <DataTable
        columns={[
          {
            key: 'corretor',
            header: 'Corretor',
            render: (aceite: TermsAcceptance) =>
              aceite.broker?.label ?? corretor.legal_name,
          },
          {
            key: 'pessoa',
            header: 'Pessoa',
            render: (aceite: TermsAcceptance) => (
              <div className="flex flex-col">
                <span className="text-ink-900">{aceite.user_label}</span>
                <span className="text-xs text-ink-500">{aceite.user_email}</span>
              </div>
            ),
          },
          {
            key: 'data',
            header: 'Data e hora da criacao',
            render: (aceite: TermsAcceptance) => formatDateTime(aceite.accepted_at),
          },
          {
            key: 'termo',
            header: 'Termo de aceite',
            align: 'right',
            render: (aceite: TermsAcceptance) => (
              <Button
                size="sm"
                variant="secondary"
                loading={baixando === aceite.id}
                aria-label={`Baixar o termo de aceite de ${aceite.user_label}`}
                onClick={() => void baixar(aceite)}
              >
                Baixar
              </Button>
            ),
          },
        ]}
        items={consulta.data?.items ?? []}
        keyOf={(aceite) => aceite.id}
        empty={<EmptyState title="Nenhum aceite do termo de uso nesta corretora" />}
      />
    </DetailCard>
  );
}

function HistoricoDeAlteracoes({ corretor }: SecaoProps) {
  return (
    <HistoryTab
      entityType="broker"
      entityId={corretor.id}
      title="Historico de Alteracoes"
      description="Toda mudanca no cadastro do corretor, inclusive socios, enderecos, responsaveis e a conta de repasse, com o antes e o depois."
      emptyText="Nenhuma alteracao registrada"
      actionLabel={rotuloDaAcao}
    />
  );
}

function HistoricoDeConsultas({ corretor }: SecaoProps) {
  const { can } = useAuth();
  const podeVer = can('integracoes.integracao.view');
  const consulta = useQuery({
    queryKey: ['integration-calls', 'corretor', corretor.id],
    queryFn: ({ signal }) =>
      integrationCalls.list(
        { subject_type: 'corretor', subject_id: corretor.id, limit: 50 },
        signal,
      ),
    enabled: podeVer,
  });
  if (!podeVer) {
    return (
      <DetailCard title="Historico de Consultas">
        <EmptyState
          title="Consulta restrita"
          description="O historico de consultas a sistemas externos exige a leitura do registro de integracoes."
        />
      </DetailCard>
    );
  }
  return (
    <DetailCard
      title="Historico de Consultas"
      description="Chamadas a sistemas externos feitas sobre esta corretora, como a consulta a Receita e a SUSEP."
      loading={consulta.isPending}
      error={consulta.error}
    >
      <DataTable
        columns={[
          {
            key: 'tipo',
            header: 'Consulta',
            render: (linha: IntegrationCall) => linha.call_type_name || linha.call_type,
          },
          {
            key: 'provedor',
            header: 'Provedor',
            render: (linha: IntegrationCall) =>
              nomeDoProvedor(linha.provider_key, linha.provider_name),
          },
          {
            key: 'situacao',
            header: 'Situacao',
            render: (linha: IntegrationCall) => linha.status,
          },
          {
            key: 'quando',
            header: 'Data',
            render: (linha: IntegrationCall) => formatDateTime(linha.started_at),
          },
        ]}
        items={consulta.data?.items ?? []}
        keyOf={(linha) => linha.id}
        empty={<EmptyState title="Nenhuma consulta registrada para a corretora" />}
      />
    </DetailCard>
  );
}

// --- Operacoes e comissoes do SeguraCore ---------------------------------------

function OperacoesDoCorretor({ corretor }: SecaoProps) {
  const consulta = useQuery({
    queryKey: ['operations', 'por-corretor', corretor.id],
    queryFn: ({ signal }) =>
      operations.list({ broker_id: corretor.id, limit: 50 }, signal),
  });
  return (
    <DetailCard title="Operacoes" loading={consulta.isPending} error={consulta.error}>
      <DataTable
        columns={[
          {
            key: 'operacao',
            header: 'Operacao',
            render: (operacao) => (
              <Link
                to={`/operacoes/${operacao.id}`}
                className="line-clamp-2 font-medium text-brand-700 hover:underline"
              >
                {operacao.subject || `Operacao ${operacao.number}`}
              </Link>
            ),
          },
          {
            key: 'tomador',
            header: 'Tomador',
            render: (operacao) => operacao.policyholder.label,
          },
          {
            key: 'valor',
            header: 'Valor garantido',
            align: 'right',
            render: (operacao) => formatMoney(operacao.insured_amount),
          },
          {
            key: 'etapa',
            header: 'Etapa',
            render: (operacao) => (
              <StatusBadge kind="operacao" value={operacao.stage} />
            ),
          },
        ]}
        items={consulta.data?.items ?? []}
        keyOf={(operacao) => operacao.id}
        empty={<EmptyState title="Nenhuma operacao aberta por este corretor" />}
      />
    </DetailCard>
  );
}

function ComissoesDoCorretor({ corretor }: SecaoProps) {
  const consulta = useQuery({
    queryKey: ['commissions', 'por-corretor', corretor.id],
    queryFn: ({ signal }) =>
      commissions.list({ broker_id: corretor.id, limit: 50 }, signal),
  });
  return (
    <DetailCard title="Comissoes" loading={consulta.isPending} error={consulta.error}>
      <DataTable
        columns={[
          {
            key: 'competencia',
            header: 'Competencia',
            render: (linha) => linha.competence,
          },
          {
            key: 'valor',
            header: 'Valor',
            align: 'right',
            render: (linha) => formatMoney(linha.amount),
          },
          {
            key: 'situacao',
            header: 'Situacao',
            render: (linha) => <StatusBadge kind="repasse" value={linha.status} />,
          },
        ]}
        items={consulta.data?.items ?? []}
        keyOf={(linha) => linha.id}
        empty={<EmptyState title="Nenhuma comissao lancada para este corretor" />}
      />
    </DetailCard>
  );
}

/**
 * O menu do detalhe, com as secoes do OnPoint na ordem dele (Cadastro Completo,
 * Nomeacoes, Socios, Termos de Aceite, Historico de Alteracoes, Historico de
 * Consultas), seguidas das secoes que o SeguraCore ja tinha (Operacoes e Comissoes).
 */
function secoesDoCorretor(can: (permissao: string) => boolean): SecaoDoMenu[] {
  return [
    { chave: 'cadastro', titulo: 'Cadastro Completo', Componente: CadastroCompleto },
    ...(can('tomadores.tomador.view')
      ? [{ chave: 'nomeacoes', titulo: 'Nomeacoes', Componente: Nomeacoes }]
      : []),
    { chave: 'socios', titulo: 'Socios', Componente: Socios },
    { chave: 'termos', titulo: 'Termos de Aceite', Componente: TermosDeAceite },
    {
      chave: 'alteracoes',
      titulo: 'Historico de Alteracoes',
      Componente: HistoricoDeAlteracoes,
    },
    {
      chave: 'consultas',
      titulo: 'Historico de Consultas',
      Componente: HistoricoDeConsultas,
    },
    ...(can('operacoes.operacao.view')
      ? [{ chave: 'operacoes', titulo: 'Operacoes', Componente: OperacoesDoCorretor }]
      : []),
    ...(can('comissoes.comissao.view')
      ? [{ chave: 'comissoes', titulo: 'Comissoes', Componente: ComissoesDoCorretor }]
      : []),
  ];
}

/** O "Atencao!" do OnPoint ao abrir o cadastro: o que pede cuidado na corretora. */
function avisosDo(corretor: Broker): string[] {
  const avisos: string[] = [];
  if (corretor.is_blocked) {
    avisos.push(
      `Corretor bloqueado em ${formatDate(corretor.blocked_at)}. Justificativa: ${corretor.block_reason || 'nao informada'}.`,
    );
  }
  if (corretor.has_pending_documents) {
    avisos.push(
      `${corretor.pending_documents_count ?? 0} documento(s) obrigatorio(s) pedido(s) e pendente(s).`,
    );
  }
  if (corretor.registration_expired && corretor.registration_expires_on) {
    avisos.push(
      `Cadastro expirado em ${formatCivilDate(corretor.registration_expires_on)}.`,
    );
  }
  if (corretor.susep_reregistration_due) avisos.push('Recadastro SUSEP pendente.');
  if (corretor.compliance_pending) avisos.push('Validacao de compliance pendente.');
  return avisos;
}

/**
 * Cadastro do corretor ("Abrir cadastro" da lista), no DetailLayout comum (P-FUN-2b),
 * com as abas do OnPoint. A gaveta da lista continua como visao rapida.
 *
 * O servidor confere empresa, permissao e o escopo da corretora em cada leitura: a
 * tela esconde o que nao abre, e nao decide o acesso.
 */
export function BrokerDetailPage() {
  const { brokerId = '' } = useParams();
  const { can, session } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [bloqueando, setBloqueando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);

  const consulta = useQuery({
    queryKey: ['broker', brokerId],
    queryFn: ({ signal }) => brokers.get(brokerId, signal),
    enabled: brokerId !== '',
  });
  // O OnPoint le o bloqueio de consultas ao abrir o cadastro (ObterBloqueioConsulta),
  // para o aviso "Atencao!".
  const limiteDeConsultas = useLimiteDeConsultas(brokerId);

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['broker', brokerId] });
    void queryClient.invalidateQueries({ queryKey: ['brokers'] });
    void queryClient.invalidateQueries({
      queryKey: ['entity-history', 'broker', brokerId],
    });
  }

  const mover = useMutation({
    mutationFn: ({
      etapa,
      justificativa,
    }: {
      etapa: string;
      justificativa?: string;
    }) =>
      justificativa === undefined
        ? brokers.transition(brokerId, etapa)
        : brokers.transition(brokerId, etapa, justificativa),
    onSuccess: (corretor) => {
      toast.success(
        `Corretor movido para ${statusLabel('corretor', corretor.stage).toLowerCase()}.`,
      );
      setBloqueando(false);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const digital = useMutation({
    mutationFn: (ativo: boolean) => brokers.setDigital(brokerId, ativo),
    onSuccess: (corretor) => {
      toast.success(
        corretor.is_digital
          ? 'Corretor digital ativado.'
          : 'Corretor digital desativado.',
      );
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  // "Consultar SUSEP" (ADR 0095): reconsulta o registro e atualiza a ficha. So informa:
  // a corretora que deixou de ser apta continua com a etapa e o acesso que tem.
  const consultarSusep = useMutation({
    mutationFn: () => brokerSusep.consult(brokerId),
    onSuccess: (resultado) => {
      const { screening } = resultado;
      if (resultado.updated && screening.proceed) toast.success(screening.message);
      else if (screening.status === 'nao_configurada') toast.info(screening.message);
      else toast.error(screening.message);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const excluir = useMutation({
    mutationFn: () => brokers.remove(brokerId),
    onSuccess: () => {
      toast.success('Corretor excluido.');
      void queryClient.invalidateQueries({ queryKey: ['brokers'] });
      void navigate('/corretores');
    },
    onError: (erro: Error) => {
      setExcluindo(false);
      toast.error(erro.message);
    },
  });

  if (consulta.isPending) return <DetailLoading />;

  if (consulta.isError) {
    return (
      <DetailError
        title="Corretor"
        alertTitle="Nao foi possivel abrir o corretor"
        message={consulta.error.message}
        onBack={() => void navigate('/corretores')}
      />
    );
  }

  const corretor = consulta.data;
  const daCorretora = session?.broker_scoped === true;
  const podeMover = can(PERMISSAO_EDITAR) && !daCorretora;
  const podeMarcarDigital = can(PERMISSAO_DIGITAL) && !daCorretora;
  const avisos = [...avisosDo(corretor), ...avisosDoLimite(limiteDeConsultas.data)];

  return (
    <>
      <DetailLayout
        title={corretor.legal_name}
        description={`CNPJ ${formatDocument(corretor.document)}`}
        navLabel="Secoes do corretor"
        meta={
          <div className="flex flex-wrap gap-1.5">
            <StatusBadge kind="corretor" value={corretor.stage} />
            <StatusBadge kind="situacaoExterna" value={corretor.external_status} />
            {corretor.susep_situation ? (
              <Badge
                tone={
                  corretor.susep_situation.toLowerCase() === 'ativo' &&
                  corretor.susep_damages_enabled
                    ? 'success'
                    : 'warning'
                }
              >
                {`SUSEP: ${corretor.susep_situation}${
                  corretor.susep_damages_enabled ? '' : ', sem Seguros de Danos'
                }`}
              </Badge>
            ) : null}
            {corretor.channel ? (
              <Badge tone="info">{corretor.channel.name}</Badge>
            ) : null}
            {corretor.has_pep_administrator ? (
              <Badge tone="warning">Administrador PEP</Badge>
            ) : null}
            {corretor.is_digital ? (
              <span title={PENDENCIA_DIGITAL}>
                <Badge tone="brand">Corretor digital</Badge>
              </span>
            ) : null}
          </div>
        }
        actions={
          <>
            <Link
              to="/corretores"
              className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-ink-700 hover:bg-ink-100"
            >
              Lista de corretores
            </Link>
            {podeMover
              ? corretor.next_stages.map((etapa) => (
                  <Button
                    key={etapa}
                    size="sm"
                    variant={etapa === 'bloqueado' ? 'danger' : 'secondary'}
                    loading={mover.isPending}
                    onClick={() =>
                      etapa === 'bloqueado'
                        ? setBloqueando(true)
                        : mover.mutate({ etapa })
                    }
                  >
                    {statusLabel('corretor', etapa)}
                  </Button>
                ))
              : null}
            {podeMover ? (
              <Button
                size="sm"
                variant="secondary"
                loading={consultarSusep.isPending}
                title="Reconsulta o registro da corretora na SUSEP e atualiza a ficha."
                onClick={() => consultarSusep.mutate()}
              >
                Consultar SUSEP
              </Button>
            ) : null}
            {podeMarcarDigital ? (
              <Button
                size="sm"
                variant="secondary"
                loading={digital.isPending}
                title={PENDENCIA_DIGITAL}
                onClick={() => digital.mutate(!corretor.is_digital)}
              >
                {corretor.is_digital
                  ? 'Desativar corretor digital'
                  : 'Ativar corretor digital'}
              </Button>
            ) : null}
            {can(PERMISSAO_EXCLUIR) && !daCorretora ? (
              <Button size="sm" variant="ghost" onClick={() => setExcluindo(true)}>
                Excluir
              </Button>
            ) : null}
          </>
        }
        banner={
          avisos.length > 0 ? (
            <Alert tone="warning" title="Atencao!">
              <ul className="flex flex-col gap-0.5">
                {avisos.map((aviso) => (
                  <li key={aviso}>{aviso}</li>
                ))}
              </ul>
            </Alert>
          ) : undefined
        }
        sections={secoesDoCorretor(can).map(({ chave, titulo, Componente }) => ({
          key: chave,
          title: titulo,
          render: () => <Componente corretor={corretor} onMudou={invalidar} />,
        }))}
      />

      <BlockBrokerDialog
        open={bloqueando}
        nome={corretor.legal_name}
        enviando={mover.isPending}
        onClose={() => setBloqueando(false)}
        onConfirmar={(justificativa) =>
          mover.mutate({ etapa: 'bloqueado', justificativa })
        }
      />

      <ConfirmDialog
        open={excluindo}
        onClose={() => setExcluindo(false)}
        onConfirm={() => excluir.mutate()}
        title={`Excluir ${corretor.legal_name}`}
        message="A exclusao so acontece quando o corretor nunca entrou em um negocio. Se ele ja intermediou alguma operacao, o cadastro permanece e o caminho e bloquea-lo."
        confirmLabel="Excluir"
        tone="danger"
        loading={excluir.isPending}
      />
    </>
  );
}
